import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  DebtAnswer,
  OnboardingAnswers,
  createEmptyDebt
} from '../../utils/onboardingCategoryBuilder';
import {
  AmountCurrencySelect,
  CardRemoveButton,
  RecurringFields,
  parseAmount
} from './OnboardingBuilderFields';

interface OnboardingDebtsStepProps {
  answers: OnboardingAnswers;
  onChange: (answers: OnboardingAnswers) => void;
}

const OnboardingDebtsStep: React.FC<OnboardingDebtsStepProps> = ({ answers, onChange }) => {
  const { t } = useTranslation();
  const defaultCurrency = answers.primaryCurrency || 'USD';
  const derivedDebts = answers.debts.filter(debt => debt.linkedAccountId);
  const manualDebts = answers.debts.filter(debt => !debt.linkedAccountId);

  const updateDebts = (debts: DebtAnswer[]) => {
    onChange({ ...answers, debts });
  };

  const updateDebtById = (debtId: string, patch: Partial<DebtAnswer>) => {
    updateDebts(
      answers.debts.map(debt => (debt.id === debtId ? { ...debt, ...patch } : debt))
    );
  };

  const removeDebt = (debtId: string) => {
    updateDebts(answers.debts.filter(debt => debt.id !== debtId));
  };

  const sourceLabel = (source: DebtAnswer['source']) => {
    if (source === 'creditCard') {
      return t('steps.debts.sources.creditCard', { ns: 'onboarding' });
    }
    if (source === 'overdraft') {
      return t('steps.debts.sources.overdraft', { ns: 'onboarding' });
    }
    return t('steps.debts.sources.other', { ns: 'onboarding' });
  };

  const renderDebtCard = (debt: DebtAnswer, fromDerived: boolean) => (
    <div key={debt.id} className="builder-card onboarding-debt-card">
      <div className="builder-card-header">
        {fromDerived ? (
          <div className="debt-derived-title">
            <strong>{debt.name}</strong>
            <span className="statement-account-type">{sourceLabel(debt.source)}</span>
          </div>
        ) : (
          <input
            type="text"
            className="builder-text-input"
            value={debt.name}
            placeholder={t('steps.debts.namePlaceholder', { ns: 'onboarding' })}
            onChange={e => updateDebtById(debt.id, { name: e.target.value })}
          />
        )}
        {!fromDerived && (
          <CardRemoveButton
            onClick={() => removeDebt(debt.id)}
            label={t('builder.actions.remove', { ns: 'onboarding' })}
          />
        )}
      </div>

      <label className="builder-field">
        <span>{t('steps.debts.totalOwedLabel', { ns: 'onboarding' })}</span>
        <p className="builder-help">{t('steps.debts.totalOwedHelp', { ns: 'onboarding' })}</p>
        <div className="builder-amount-row">
          <input
            type="text"
            inputMode="decimal"
            className="builder-amount-input"
            value={debt.totalOwed != null ? String(debt.totalOwed) : ''}
            placeholder="0"
            onChange={e =>
              updateDebtById(debt.id, { totalOwed: parseAmount(e.target.value) })
            }
          />
          <AmountCurrencySelect
            value={debt.totalOwedCurrency || defaultCurrency}
            onChange={currency =>
              updateDebtById(debt.id, { totalOwedCurrency: currency })
            }
          />
        </div>
      </label>

      <div className="debt-payment-section">
        <p className="builder-sublabel">{t('steps.debts.paymentLabel', { ns: 'onboarding' })}</p>
        <p className="builder-help">{t('steps.debts.paymentHelp', { ns: 'onboarding' })}</p>
        <RecurringFields
          bill={debt.payment}
          defaultCurrency={debt.totalOwedCurrency || defaultCurrency}
          amountLabel={t('steps.debts.paymentAmount', { ns: 'onboarding' })}
          onChange={payment => updateDebtById(debt.id, { payment })}
        />
      </div>
    </div>
  );

  return (
    <div className="onboarding-debts-step">
      {derivedDebts.length > 0 && (
        <section className="builder-section">
          <h4>{t('steps.debts.fromAccountsTitle', { ns: 'onboarding' })}</h4>
          <p className="builder-help">{t('steps.debts.fromAccountsHelp', { ns: 'onboarding' })}</p>
          {derivedDebts.map(debt => renderDebtCard(debt, true))}
        </section>
      )}

      <section className="builder-section">
        <h4>{t('steps.debts.otherTitle', { ns: 'onboarding' })}</h4>
        <p className="builder-help">{t('steps.debts.otherHelp', { ns: 'onboarding' })}</p>
        {manualDebts.map(debt => renderDebtCard(debt, false))}
        <button
          type="button"
          className="builder-add"
          onClick={() =>
            updateDebts([
              ...answers.debts,
              createEmptyDebt(manualDebts.length, defaultCurrency)
            ])
          }
        >
          + {t('steps.debts.add', { ns: 'onboarding' })}
        </button>
      </section>
    </div>
  );
};

export default OnboardingDebtsStep;
